/**
 * Catalog filters, sort and paging (sections/main-collection.liquid).
 * Changes re-render the section in place via the Section Rendering API and update the URL, so
 * results are shareable and the back button works. Open/closed filter groups are kept across
 * updates. Below 1024px the filter sidebar is a slide-in panel.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-sort-select]')) {
        this.querySelector('[data-filter-form] [name="sort_by"]').value = event.target.value;
        this.submit();
      } else if (event.target.closest('[data-filter-form]') && !event.target.matches('[data-price-input], [data-filter-search]')) {
        this.submit();
      }
    });

    // Price inputs: wait for the shopper to stop typing.
    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-price-input]')) {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.submit(), 600);
      } else if (event.target.matches('[data-filter-search]')) {
        this.filterOptions(event.target);
      }
    });

    this.addEventListener('submit', (event) => {
      if (event.target.matches('[data-filter-form]')) {
        event.preventDefault();
        this.submit();
      }
    });

    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-filter-link]');
      if (link) {
        event.preventDefault();
        this.render(link.href);
        if (link.closest('.catalog__pagination')) this.scrollIntoView({ block: 'start' });
        return;
      }
      if (event.target.closest('[data-filters-open]')) this.toggleFilters(true);
      else if (event.target.closest('[data-filters-close]')) this.toggleFilters(false);
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('filters-open')) this.toggleFilters(false);
    };
    document.addEventListener('keydown', this.onKeydown);

    this.onPopState = () => this.render(window.location.href, { push: false });
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('popstate', this.onPopState);
  }

  toggleFilters(open) {
    this.classList.toggle('filters-open', open);
    if (open) this.querySelector('[data-filters-panel] summary, [data-filters-panel] input')?.focus();
    else this.querySelector('[data-filters-open]')?.focus();
  }

  /** Builds the URL from the filter form (dropping empty values) and renders it. */
  submit() {
    const form = this.querySelector('[data-filter-form]');
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    this.render(`${window.location.pathname}?${params}`);
  }

  async render(url, { push = true } = {}) {
    const target = new URL(url, window.location.origin);
    const openGroups = [...this.querySelectorAll('[data-filter-group][open]')].map((group) => group.id);
    const panelOpen = this.classList.contains('filters-open');
    const focusedName = document.activeElement?.name;

    this.controller?.abort();
    this.controller = new AbortController();
    this.classList.add('is-loading');

    try {
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const response = await fetch(fetchUrl, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('collection-filters');
      if (!fresh) throw new Error('Missing catalog markup');

      this.innerHTML = fresh.innerHTML;
      // Keep the shopper's open filter groups (and any with active values, which render open).
      openGroups.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));
      this.classList.toggle('filters-open', panelOpen);
      if (focusedName) this.querySelector(`[name="${CSS.escape(focusedName)}"]`)?.focus({ preventScroll: true });

      if (push) window.history.pushState({}, '', target.pathname + target.search);
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = target.href; // fall back to a full load
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /** Narrows a long filter list (e.g. categories) to options matching the typed text. */
  filterOptions(input) {
    const query = input.value.trim().toLowerCase();
    input
      .closest('.filter__body')
      .querySelectorAll('[data-filter-option]')
      .forEach((option) => {
        option.hidden = query !== '' && !option.dataset.filterOption.includes(query);
      });
  }
}
customElements.define('collection-filters', CollectionFilters);
